import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  FirestoreDataConverter,
  Unsubscribe,
} from 'firebase/firestore';
import type { Review } from '@segmently/schema-models/entities';
import { CoreFirebaseService } from './CoreFirebaseService';
import { reviewConverter as clientReviewConverter } from '../converters/reviewConverter';

/**
 * ReviewService
 *
 * Type-safe service for Review entity CRUD operations.
 * Reviews are stored as subcollection under projects.
 *
 * Path: projects/{projectId}/reviews/{reviewId}
 *
 * Features:
 * - Basic CRUD operations (create, read, delete)
 * - Type-safe: only Review types exposed, no Firebase types in public API
 * - Uses reviewConverter for Date↔Timestamp conversion
 *
 * Usage:
 * ```typescript
 * import { ReviewService } from '@/modules/firebase-core/services/ReviewService';
 *
 * const service = new ReviewService('project-id');
 *
 * // Create
 * const review = await service.create({
 *   nickname: 'John',
 *   rating: 5,
 *   reviewtext: 'Great app!',
 *   reviewdate: new Date()
 * });
 *
 * // Read
 * const found = await service.get(review.id!);
 *
 * // Delete
 * await service.delete(review.id!);
 * ```
 */
export class ReviewService {
  private readonly collectionName = 'reviews';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<Review>;
  private readonly projectId: string;

  /**
   * Create a new ReviewService instance
   *
   * @param projectId - Parent project ID
   * @param converter - Firestore converter for Review entity (defaults to client SDK converter)
   */
  constructor(projectId: string, converter: FirestoreDataConverter<Review> = clientReviewConverter) {
    this.projectId = projectId;
    this.converter = converter;
  }

  /**
   * Create a new review
   *
   * @param data - Partial review data (id will be auto-generated if not provided)
   * @returns {Promise<Review>} The created review with generated ID
   *
   * @example
   * ```typescript
   * const review = await service.create({
   *   nickname: 'Alice',
   *   rating: 5,
   *   reviewtext: 'Excellent product!',
   *   reviewdate: new Date()
   * });
   * ```
   */
  async create(data: Partial<Review>): Promise<Review> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName);
      const docRef = doc(collectionRef).withConverter(this.converter);

      const reviewData: Review = {
        ...data,
        id: docRef.id,
        createdAt: new Date(),
      } as Review;

      await setDoc(docRef, reviewData);

      return reviewData;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ReviewService.create');
      throw error;
    }
  }

  /**
   * Get a review by ID
   *
   * @param id - Review ID
   * @returns {Promise<Review | null>} The review or null if not found
   *
   * @example
   * ```typescript
   * const review = await service.get('review-123');
   * if (review) {
   *   console.log(review.nickname);
   * }
   * ```
   */
  async get(id: string): Promise<Review | null> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id)
        .withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as Review;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ReviewService.get');
      throw error;
    }
  }

  /**
   * Delete a review
   *
   * @param id - Review ID
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await service.delete('review-123');
   * ```
   */
  async delete(id: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ReviewService.delete');
      throw error;
    }
  }

  /**
   * Update a review
   *
   * @param id - Review ID
   * @param data - Partial review data to merge
   * @returns {Promise<void>}
   */
  async update(id: string, data: Partial<Review>): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'projects', this.projectId, this.collectionName, id);
      await updateDoc(docRef, data as Record<string, unknown>);
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ReviewService.update');
      throw error;
    }
  }

  /**
   * Subscribe to real-time review updates
   *
   * @param onData - Callback with updated reviews array
   * @param onError - Optional error callback
   * @returns {Unsubscribe} Unsubscribe function
   */
  subscribe(
    onData: (items: Review[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
      .withConverter(this.converter);
    const q = query(collectionRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      onData(snap.docs.map(d => ({ ...d.data(), id: d.id } as Review)));
    }, onError);
  }

  /**
   * List reviews for the project
   *
   * @param options - Query options
   * @param options.limit - Maximum number of reviews to return (default: 20)
   * @returns {Promise<Review[]>} Array of reviews
   *
   * @example
   * ```typescript
   * const reviews = await service.list({ limit: 50 });
   * ```
   */
  async list(options: { limit?: number } = {}): Promise<Review[]> {
    try {
      const collectionRef = collection(this.firestore, 'projects', this.projectId, this.collectionName)
        .withConverter(this.converter);

      const pageLimit = options.limit || 20;
      const q = query(collectionRef, orderBy('createdAt', 'desc'), limit(pageLimit));

      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as Review));
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'ReviewService.list');
      throw error;
    }
  }
}
